import { PayloadAction } from '@reduxjs/toolkit';
import { FetchError } from '@yandex-360/telemost-sdk';
import { isErrorAnswer } from '@yandex-360/telemost-sdk/api';
import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { all, takeEvery } from 'redux-saga/effects';
import { call, put, select } from 'typed-redux-saga';

import { cancelCallAction, setCloseRoomInitiatedBySelf } from '../../../pages/call/features/conference/slice';
import { toggleMuteAction } from '../../../pages/call/features/device/slice';
import { unshareScreenAction } from '../../../pages/call/features/screen-sharing/slice';
import {
    getConferenceConfig,
    getConferenceSettings,
    getMyUserId,
    getParticipant,
    getParticipantById,
    getParticipantByIds,
    getParticipantByUid,
    selectConference,
    selectIsPermissionsExtendedEnabled,
} from '../../../pages/call/selectors';
import { NotificationId } from '../../../pages/call/types';
import { i18n } from '../../helpers/i18n';
import { ACTION, FEATURE } from '../../helpers/logger-enums';
import { createLogger } from '../../helpers/loggerFacade';
import { countMetrika } from '../../helpers/metrika';
import { cloudApi } from '../../helpers/transport';
import { isRoomAgentMode } from '../config/selectors';
import { NOTIFICATIONS_MAP, notify } from '../notifications/ps-notifications';
import {
    closeRoom,
    kickPeer,
    KickPeerPayload,
    makeUserAnMember,
    makeUserAnModerator,
    muteAllPeerAudio,
    muteAllPeerReactions,
    muteAllPeerVideo,
    muteAllRecordings,
    mutePeerAudio,
    mutePeerReaction,
    mutePeerRecording,
    mutePeerScreenSharing,
    mutePeerVideo,
    muteRecordingCommand,
    setBroadcastAccessLevel,
    setConferenceAccessLevel,
} from './slice';
import { ModerationCommand, ModerationCommandObject, ModerationSourceType } from './types';

const logger = createLogger('moderation');

/**
 * Нотификация об ошибке модерации
 *
 * @param {Error} error
 * @param {string} text
 */
function* moderationErrorSaga(error: Error | unknown, text: string) {
    yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_ERROR](text)));

    logger.error(error, {
        method: 'moderationErrorSaga',
    });
}

/**
 * Метрика модерации
 *
 * @param {string} command
 */
function* countModerationMetrica(command: string) {
    const { sessionId } = yield* select();
    yield* call(countMetrika, 'meeting_screen', 'moderation', command, sessionId);
}

/**
 * Ручка для отправки команд
 *
 * @param {ApiCommand} command
 * @param {string} params
 */
export function* sendCloudApiCommand(
    command: CloudAPI.SendCommand.ApiCommand,
    params: Omit<CloudAPI.SendCommand.Request, 'command'>
) {
    const config = yield* select(getConferenceConfig);
    const uri = config?.resolved_uri;

    if (uri) {
        yield* call([cloudApi, cloudApi.sendCommand], uri, { ...params, command });
    }
}

/**
 * Замьютить аудио участника
 */
function* mutePeerAudioSaga({ payload: peerId }: ReturnType<typeof mutePeerAudio>): Generator {
    try {
        yield* call(sendCloudApiCommand, 'mute-microphone', { peerId });
        yield* call(countModerationMetrica, 'mic_off');
    } catch (error) {
        yield* call(moderationErrorSaga, error, i18n('moderation', 'notification-action-error-mute-audio'));
    }
}

/**
 * Замьютить видео участника
 */
function* mutePeerVideoSaga({ payload: peerId }: ReturnType<typeof mutePeerVideo>): Generator {
    try {
        yield* call(sendCloudApiCommand, 'mute-camera', { peerId });
        yield* call(countModerationMetrica, 'camera_off');
    } catch (error) {
        yield* call(moderationErrorSaga, error, i18n('moderation', 'notification-action-error-mute-video'));
    }
}

/**
 * Отключить шаринг участника
 */
function* mutePeerScreenSharingSaga({ payload: peerId }: ReturnType<typeof mutePeerScreenSharing>): Generator {
    try {
        yield* call(sendCloudApiCommand, 'mute-desktop', { peerId });
        yield* call(countModerationMetrica, 'sharing_off');
    } catch (error) {
        yield* call(moderationErrorSaga, error, i18n('moderation', 'notification-action-error-mute-screensharing'));
    }
}

/**
 * Отключить рекцию участника участника
 */
function* mutePeerReactionSaga({ payload: peerId }: ReturnType<typeof mutePeerScreenSharing>): Generator {
    try {
        yield* call(sendCloudApiCommand, 'mute-reaction', { peerId });
        yield* call(countMetrika, 'meeting_screen', 'hand_and_reactions', 'admin_hand_down_participant');
    } catch (error) {
        yield* call(countMetrika, 'meeting_screen', 'hand_and_reactions', 'error');
        yield* call(moderationErrorSaga, error, i18n('moderation', 'notification-action-error'));
    }
}

/**
 * Отключить локальную запись участника
 */
function* mutePeerRecordingSaga({ payload: { peerId, withoutRemoval } }: ReturnType<typeof mutePeerRecording>) {
    const { participantsByIds } = yield* select(selectConference);
    const displayName = participantsByIds[peerId]?.displayName;
    try {
        yield* call(sendCloudApiCommand, 'mute-recording', { peerId, withoutRemoval });
        yield* put(
            notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_MUTE_RECORDING_SUCCESS](withoutRemoval, displayName))
        );
    } catch (error) {
        logger.error(error, {
            method: 'mutePeerRecordingSaga',
        });
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_MUTE_RECORDING_ERROR](displayName)));
    }
}

/**
 * Отключить локальную запись участника
 */
function* muteAllRecordingsSaga({ payload }: ReturnType<typeof muteAllRecordings>) {
    const myPeerId = yield* select(getMyUserId);
    try {
        yield* call(sendCloudApiCommand, 'mute-all-recordings', { withoutRemoval: payload, excludePeerId: myPeerId });
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_MUTE_ALL_RECORDING_SUCCESS](payload)));
    } catch (error) {
        logger.error(error, {
            method: 'muteAllRecordingsSaga',
        });

        if (FetchError.is(error) && error.status === 403) {
            return yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_NO_PERMISSION_TO_MUTE_RECORDINGS]()));
        }
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_MUTE_ALL_RECORDINGS_ERROR]()));
    }
}

/**
 * Замьютить аудио всех участников
 */
function* muteAllPeerAudioSaga(): Generator {
    try {
        const myPeerId = yield* select(getMyUserId);
        yield* call(sendCloudApiCommand, 'mute-all-microphones', { excludePeerId: myPeerId });
        yield* call(countModerationMetrica, 'mic_all_off');
    } catch (error) {
        yield* call(moderationErrorSaga, error, i18n('moderation', 'notification-action-error-mute-all-audio'));
    }
}

/**
 * Замьютить видео всех участников
 */
function* muteAllPeerVideoSaga(): Generator {
    try {
        const myPeerId = yield* select(getMyUserId);
        yield* call(sendCloudApiCommand, 'mute-all-cameras', { excludePeerId: myPeerId });
        yield* call(countModerationMetrica, 'camera_all_off');
    } catch (error) {
        yield* call(moderationErrorSaga, error, i18n('moderation', 'notification-action-error-mute-all-video'));
    }
}

/**
 * Сбросить реакции всем участникам
 */
function* muteAllPeerReactionsSaga(): Generator {
    try {
        const myPeerId = yield* select(getMyUserId);
        yield* call(sendCloudApiCommand, 'mute-all-reactions', { excludePeerId: myPeerId });
        yield* call(countMetrika, 'meeting_screen', 'hand_and_reactions', 'admin_hand_down_all');
    } catch (error) {
        yield* call(countMetrika, 'meeting_screen', 'hand_and_reactions', 'error');
        yield* call(moderationErrorSaga, error, i18n('moderation', 'notification-action-error'));
    }
}

/**
 * Сделать юзера модератором
 */
function* makeUserAnModeratorSaga({ payload: peerId }: ReturnType<typeof makeUserAnModerator>): Generator {
    const participantsByIds = yield* select(getParticipantByIds);
    const participant = participantsByIds[peerId];
    const userUid = participant?.uid;
    try {
        if (userUid) {
            yield* call(sendCloudApiCommand, 'set-user-role', { targetUid: userUid, role: 'ADMIN' });
            yield* call(countModerationMetrica, 'organizator_assigned');
        }
    } catch (error) {
        logger.error(error, {
            method: 'makeUserAnModeratorSaga',
            additional: {
                objectPeerId: peerId,
                objectUid: userUid,
            },
        });

        const isPermissionsExtendedEnabled = yield* select(selectIsPermissionsExtendedEnabled);
        if (isPermissionsExtendedEnabled) {
            return yield* put(
                notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_MAKE_ADMIN_ERROR](participant?.displayName))
            );
        }
        yield* call(moderationErrorSaga, error, i18n('moderation', 'notification-action-error-make-an-co-host'));
    }
}

/**
 * Сделать юзера модератором
 */
function* makeUserAnMemberSaga({ payload: peerId }: ReturnType<typeof makeUserAnMember>): Generator {
    const participantsByIds = yield* select(getParticipantByIds);
    const participant = participantsByIds[peerId];
    const userUid = participant?.uid;
    try {
        if (userUid) {
            yield* call(sendCloudApiCommand, 'set-user-role', { targetUid: userUid, role: 'MEMBER' });
        }
    } catch (error) {
        logger.error(error, {
            method: 'makeUserAnMemberSaga',
            additional: {
                objectPeerId: peerId,
                objectUid: userUid,
            },
        });
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_MAKE_MEMBER_ERROR](participant?.displayName)));
    }
}

/**
 * Прием событий модераций
 */
export function* onModerationCommandReceiveSaga({ command, source }: ModerationCommandObject): Generator {
    // Игнорируем команды модерации для рум-агента версии 2
    // Причина описана тут - https://st.yandex-team.ru/TELEMOSTFRONT-1948
    const isRoomAgent = yield* select(isRoomAgentMode);
    if (isRoomAgent) {
        return;
    }

    const state = yield* select();
    const participant =
        source.type === ModerationSourceType.USER_SOURCE
            ? yield* call(getParticipantByUid, state, source.uid)
            : yield* select((state) => getParticipant(state, source.peer_id));

    switch (command.type) {
        case ModerationCommand.MUTE_AUDIO:
            yield* put(toggleMuteAction({ value: true, type: 'audio' }));
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_MUTE_AUDIO](participant?.isOwner)));
            break;

        case ModerationCommand.MUTE_VIDEO:
            yield* put(toggleMuteAction({ value: true, type: 'video' }));
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_MUTE_VIDEO](participant?.isOwner)));
            break;

        case ModerationCommand.MUTE_DESKTOP:
            yield* put(unshareScreenAction());
            if (source.type === ModerationSourceType.USER_SOURCE) {
                yield* put(
                    notify(
                        NOTIFICATIONS_MAP[NotificationId.MODERATION_MUTE_SCREEN_SHARING](
                            participant?.isOwner,
                            source.uid
                        )
                    )
                );
            }
            break;
        case ModerationCommand.MUTE_RECORDING:
            yield* put(muteRecordingCommand());
            break;
        case ModerationCommand.MUTE_RECORDING_WITHOUT_REMOVAL:
            yield* put(muteRecordingCommand(true));
            break;
    }
}

/**
 * Ручка изменения настроек доступа
 */
function* setConferenceAccessLevelSaga({ payload: accessLevel }: ReturnType<typeof setConferenceAccessLevel>) {
    try {
        const config = yield* select(getConferenceConfig);

        const uri = config?.resolved_uri;

        if (uri) {
            yield* call([cloudApi, cloudApi.setConferenceAccessLevel], uri, accessLevel);

            logger.endFeatureDelta({
                feature: FEATURE.WAITING_ROOM,
                action: ACTION.CHANGE_PRIVACY,
                isSuccess: true,
            });
        }
    } catch (error) {
        yield* call(
            moderationErrorSaga,
            error,
            i18n('moderation', 'notification-action-error-set-conference-access-level')
        );

        logger.endFeatureDelta({
            feature: FEATURE.WAITING_ROOM,
            action: ACTION.CHANGE_PRIVACY,
            isSuccess: false,
        });
    }
}

/**
 * Ручка изменения настроек доступа
 */
function* setBroadcastAccessLevelSaga({ payload: accessLevel }: ReturnType<typeof setBroadcastAccessLevel>) {
    try {
        const config = yield* select(getConferenceConfig);
        const settings = yield* select(getConferenceSettings);
        const uri = config?.resolved_uri;
        const broadcastUri = settings?.broadcast_data?.broadcast_uri;

        if (uri && broadcastUri) {
            yield* call([cloudApi, cloudApi.setBroadcastAccessLevel], uri, broadcastUri, accessLevel);
        }
    } catch (error) {
        yield* call(
            moderationErrorSaga,
            error,
            i18n('moderation', 'notification-action-error-set-broadcast-access-level')
        );
    }
}

/**
 * Удалить участника
 */
function* kickPeerSaga({ payload: { peerId, withBan } }: PayloadAction<KickPeerPayload>): Generator {
    try {
        const participant = yield* select(getParticipantById, peerId);

        yield* call(sendCloudApiCommand, 'kick', { peerId, withBan });
        yield* call(countMetrika, 'meeting_screen', 'moderation', 'kick', withBan ? 'success_with_ban' : 'success');
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.MODERATION_KICK_BAN](withBan, participant?.displayName)));
    } catch (error) {
        if (isErrorAnswer(error)) {
            const { error: type, description } = error;
            yield* call(countMetrika, 'meeting_screen', 'moderation', 'kick', type || '', description || '');
        } else {
            yield* call(countMetrika, 'meeting_screen', 'moderation', 'kick');
        }
        yield* call(moderationErrorSaga, error, i18n('moderation', 'notification-action-error-kick'));
    }
}

/**
 * Завершить встречу для всех
 */
function* closeRoomSaga(): Generator {
    yield* put(setCloseRoomInitiatedBySelf(true));

    try {
        yield* call(sendCloudApiCommand, 'close-room', {});
        yield* put(cancelCallAction());
    } catch (error) {
        yield* put(setCloseRoomInitiatedBySelf(false));

        logger.error(error, {
            method: 'closeRoomSaga',
        });

        const isPermissionsExtendedEnabled = yield* select(selectIsPermissionsExtendedEnabled);
        if (isPermissionsExtendedEnabled && FetchError.is(error) && error.status === 403) {
            return yield* put(notify(NOTIFICATIONS_MAP[NotificationId.NO_PERMISSION_TO_CLOSE_ROOM]()));
        }
        yield* put(cancelCallAction());
    }
}

/**
 * rootSaga
 */
export default function* (): Generator {
    yield all([
        takeEvery(muteAllPeerAudio, muteAllPeerAudioSaga),
        takeEvery(muteAllPeerVideo, muteAllPeerVideoSaga),
        takeEvery(muteAllPeerReactions, muteAllPeerReactionsSaga),
        takeEvery(mutePeerReaction, mutePeerReactionSaga),
        takeEvery(mutePeerRecording, mutePeerRecordingSaga),
        takeEvery(muteAllRecordings, muteAllRecordingsSaga),
        takeEvery(mutePeerAudio, mutePeerAudioSaga),
        takeEvery(mutePeerScreenSharing, mutePeerScreenSharingSaga),
        takeEvery(mutePeerVideo, mutePeerVideoSaga),
        takeEvery(makeUserAnModerator, makeUserAnModeratorSaga),
        takeEvery(makeUserAnMember, makeUserAnMemberSaga),
        takeEvery(setConferenceAccessLevel, setConferenceAccessLevelSaga),
        takeEvery(setBroadcastAccessLevel, setBroadcastAccessLevelSaga),
        takeEvery(kickPeer, kickPeerSaga),
        takeEvery(closeRoom, closeRoomSaga),
    ]);
}
